import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/apps/web/components/ui/dialog";
import { Button } from "@/apps/web/components/ui/button";
import { Label } from "@/apps/web/components/ui/label";
import { Input } from "@/apps/web/components/ui/input";
import { cn } from "@/packages/utils/cn";

interface Connection {
  id: string;
  repoName: string;
  repoUrl: string;
}

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  projectId: string;
  onGenerated?: () => void;
}

function isoDateInputDefault(daysAgo = 7): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

export function ReleaseGenerateDialog({
  open,
  onOpenChange,
  projectId,
  onGenerated,
}: Props) {
  const [connections, setConnections] = useState<Connection[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [fromDate, setFromDate] = useState(isoDateInputDefault(7));
  const [toDate, setToDate] = useState(isoDateInputDefault(0));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    fetch(`/api/projects/${projectId}/github-connections`)
      .then((r) => r.json())
      .then((d: Connection[]) => {
        setConnections(d);
        setSelected(new Set(d.map((c) => c.id)));
      });
  }, [open, projectId]);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(
        `/api/projects/${projectId}/releases/generate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            connectionIds: Array.from(selected),
            fromDate: new Date(fromDate).toISOString(),
            toDate: new Date(toDate).toISOString(),
          }),
        },
      );
      if (!res.ok) throw new Error((await res.json()).error ?? "Failed");
      onOpenChange(false);
      onGenerated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Generate release</DialogTitle>
          <DialogDescription>
            {connections.length === 0
              ? "No GitHub repos connected — we'll create a blank draft for you to write by hand."
              : "We'll fetch commits from your selected GitHub repos and draft release notes."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {connections.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              You can connect a repo later in Release Settings → General to
              auto-draft from commits.
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Repositories</Label>
              <div className="space-y-1.5 max-h-48 overflow-auto">
                {connections.map((c) => {
                  const checked = selected.has(c.id);
                  return (
                    <label
                      key={c.id}
                      className={cn(
                        "flex items-center gap-2 p-2 border rounded-md cursor-pointer text-sm",
                        checked
                          ? "border-foreground bg-panel"
                          : "border-rule hover:bg-panel/50",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = new Set(selected);
                          if (checked) next.delete(c.id);
                          else next.add(c.id);
                          setSelected(next);
                        }}
                      />
                      <span className="font-mono text-xs">{c.repoName}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="from">From</Label>
              <Input
                id="from"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="to">To</Label>
              <Input
                id="to"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          </div>

          {error && <div className="text-sm text-signal-red">{error}</div>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={
              submitting || (connections.length > 0 && selected.size === 0)
            }
          >
            {submitting
              ? "Queuing…"
              : connections.length === 0
                ? "Create draft"
                : "Generate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
